/* ═════════════════════════════════════════════════════════════════
   Vinci Sovereign Ledger — Admin-Only Parallel Invoice Archive
   Firewall: 100% admin surface. No buyer/seller flow is touched.
   ═════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, Search, RefreshCw, FileText, Landmark,
  Filter, History as HistoryIcon, Send, Mail, X, Trash2, Plus,
  Scale, FilePlus2,
} from "lucide-react";

const inr = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const HEADERS = [
  ["", "All"], ["trade", "Trade"], ["service", "Service"],
  ["commission", "Commission"], ["custom", "Custom"],
];

const STATUSES = [
  ["", "Any status"], ["paid", "Paid"], ["sent", "Sent"], ["draft", "Draft"],
];

export function AdminSovereignLedger({ api }) {
  const [mode, setMode] = useState("invoices"); // "invoices" | "quotations"
  const [quotes, setQuotes] = useState([]);
  const [quoteKpi, setQuoteKpi] = useState({ count: 0, gross_total: 0 });
  const [quoteTotal, setQuoteTotal] = useState(0);
  // Trade Terms editor
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsHeader, setTermsHeader] = useState("trade");
  const [termsList, setTermsList] = useState([]);
  const [termsDefaults, setTermsDefaults] = useState([]);
  const [termsMeta, setTermsMeta] = useState({});
  const [termsBusy, setTermsBusy] = useState(false);

  const [rows, setRows] = useState([]);
  const [kpi, setKpi] = useState({ count: 0, gross_total: 0, gst_total: 0, paid_count: 0 });
  const [defaults, setDefaults] = useState({ vinci_pan: false, updated_by: null, updated_at: null });
  const [defaultsBusy, setDefaultsBusy] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [header, setHeader] = useState("");
  const [status, setStatus] = useState("");
  // Per-row toggle state — keyed by invoice id
  // { id: { vinci_gstin, vinci_pan, buyer_gstin, vinci_contact, buyer_contact } }
  const [toggles, setToggles] = useState({});
  const [busy, setBusy] = useState(null);       // id currently rendering
  const [sharing, setSharing] = useState(null); // row being shared
  const [shareTo, setShareTo] = useState("");
  const [shareCc, setShareCc] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  // Audit strip
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", String(limit));
      if (q) qs.set("q", q);
      if (mode === "invoices" && header) qs.set("header", header);
      if (status) qs.set("status", status);
      const path = mode === "quotations"
        ? "/admin/sovereign-ledger/quotations"
        : "/admin/sovereign-ledger";
      const { data } = await api.get(`${path}?${qs}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setKpi(data.kpi || kpi);
      if (data.defaults) setDefaults(data.defaults);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ledger load failed");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, page, limit, q, header, status, mode]);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sovereign-ledger/audit/recent?limit=8");
      setAudit(data.rows || []);
    } catch { /* silent */ }
  }, [api]);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", String(limit));
      if (q) qs.set("q", q);
      if (status) qs.set("status", status);
      const { data } = await api.get(`/admin/sovereign-ledger/quotations?${qs}`);
      setQuotes(data.rows || []);
      setQuoteTotal(data.total || 0);
      setQuoteKpi(data.kpi || { count: 0, gross_total: 0 });
      if (data.defaults) setDefaults(data.defaults);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quotations load failed");
    } finally { setLoading(false); }
  }, [api, page, limit, q, status]);

  useEffect(() => {
    if (mode === "quotations") loadQuotes();
    else load();
    loadAudit();
  }, [mode, load, loadAudit, loadQuotes]);

  const setToggle = (id, k, v) => setToggles((t) => ({
    ...t, [id]: {
      vinci_gstin: false, vinci_pan: false,
      buyer_gstin: true, vinci_contact: true, buyer_contact: true,
      ...(t[id] || {}), [k]: v,
    },
  }));
  const rowToggles = (id) => toggles[id] || {
    vinci_gstin: false, vinci_pan: !!defaults.vinci_pan,
    buyer_gstin: true, vinci_contact: true, buyer_contact: true,
  };

  const saveDefaults = async (vinci_pan) => {
    setDefaultsBusy(true);
    try {
      const { data } = await api.post("/admin/sovereign-ledger/defaults",
        { vinci_pan });
      setDefaults(data);
      toast.success(`Vinci PAN visibility default: ${vinci_pan ? "Shown" : "Hidden"} on every new invoice & quotation.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save default");
    } finally { setDefaultsBusy(false); }
  };

  const _qs = (t) => new URLSearchParams({
    include_gst: String(!!t.vinci_gstin),
    include_pan: String(!!t.vinci_pan),
    buyer_gstin: String(!!t.buyer_gstin),
    vinci_contact: String(!!t.vinci_contact),
    buyer_contact: String(!!t.buyer_contact),
    layout: "bureaucratic",
  }).toString();

  const gen = async (row) => {
    const t = rowToggles(row.id);
    setBusy(row.id);
    try {
      const path = mode === "quotations"
        ? `/admin/sovereign-ledger/quotations/${encodeURIComponent(row.id)}/pdf?${_qs(t)}`
        : `/admin/sovereign-ledger/${encodeURIComponent(row.id)}/pdf?${_qs(t)}`;
      const res = await api.post(path, {}, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const label = row.invoice_number || row.quote_number || row.id;
      a.download = `Vinci-Sovereign-${mode === "quotations" ? "Quotation-" : ""}${String(label).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Rendered ${label}`);
      loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Render failed");
    } finally { setBusy(null); }
  };

  const openShare = (row) => {
    setSharing(row);
    setShareTo(row.buyer_email || "");
    setShareCc("dineshsingh.legal@gmail.com");
    setShareNote("");
  };

  const doShare = async () => {
    if (!sharing) return;
    if (!shareTo || !shareTo.includes("@")) {
      toast.error("Enter the recipient email.");
      return;
    }
    const cc = shareCc.split(",").map((s) => s.trim()).filter(Boolean);
    const t = rowToggles(sharing.id);
    setShareBusy(true);
    try {
      const path = mode === "quotations"
        ? `/admin/sovereign-ledger/quotations/${encodeURIComponent(sharing.id)}/share`
        : `/admin/sovereign-ledger/${encodeURIComponent(sharing.id)}/share`;
      const body = mode === "quotations"
        ? { to_email: shareTo.trim(), cc, note: shareNote,
            include_gst: !!t.vinci_gstin, include_pan: !!t.vinci_pan,
            buyer_gstin: !!t.buyer_gstin, vinci_contact: !!t.vinci_contact,
            buyer_contact: !!t.buyer_contact }
        : { to_email: shareTo.trim(), cc, note: shareNote,
            include_gst: !!t.vinci_gstin, include_pan: !!t.vinci_pan,
            buyer_gstin: !!t.buyer_gstin, vinci_contact: !!t.vinci_contact,
            buyer_contact: !!t.buyer_contact, layout: "bureaucratic" };
      const { data } = await api.post(path, body);
      if (data.ok) {
        toast.success(`Sent to ${data.delivered_to.join(", ")}`);
        setSharing(null);
        loadAudit();
      } else {
        const errs = (data.failed || []).map((f) => `${f.email}: ${f.error}`).join("; ");
        toast.error(`Send failed. ${errs || "No inbox accepted the message."}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setShareBusy(false); }
  };

  // Trade Terms editor loaders
  const loadTerms = useCallback(async (headerKey) => {
    setTermsBusy(true);
    try {
      const { data } = await api.get(`/admin/sovereign-ledger/terms/${headerKey}`);
      const t = [...(data.terms || [])];
      while (t.length < 6) t.push("");
      setTermsList(t.slice(0, 8));
      setTermsMeta({ is_custom: !!data.is_custom, defaults: data.defaults || [],
                       updated_by: data.updated_by, updated_at: data.updated_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load terms");
    } finally { setTermsBusy(false); }
  }, [api]);

  const saveTerms = async () => {
    setTermsBusy(true);
    try {
      const clean = termsList.map((t) => (t || "").trim()).filter(Boolean);
      const { data } = await api.post(`/admin/sovereign-ledger/terms/${termsHeader}`,
        { terms: clean });
      const t = [...(data.terms || [])];
      while (t.length < 6) t.push("");
      setTermsList(t.slice(0, 8));
      setTermsMeta({ is_custom: !!data.is_custom, defaults: data.defaults || [],
                       updated_by: data.updated_by, updated_at: data.updated_at });
      toast.success(`Trade Terms saved for ${termsHeader.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setTermsBusy(false); }
  };

  const revertTerms = async () => {
    if (!window.confirm("Revert to the default six-clause board policy?")) return;
    setTermsBusy(true);
    try {
      const { data } = await api.post(`/admin/sovereign-ledger/terms/${termsHeader}`,
        { terms: [] });
      const t = [...(data.terms || [])];
      while (t.length < 6) t.push("");
      setTermsList(t.slice(0, 8));
      setTermsMeta({ is_custom: false, defaults: data.defaults || [] });
      toast.success("Reverted to default Trade Terms");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally { setTermsBusy(false); }
  };

  const openTermsEditor = async () => {
    setTermsOpen(true);
    await loadTerms(termsHeader);
  };

  // Aliases for the modal JSX (older handler names kept for JSX stability)
  const switchTermsHeader = (h) => setTermsHeader(h);
  const setTermAt = (i, v) => setTermsList((L) => {
    const next = [...L]; next[i] = v; return next;
  });
  const addTerm = () => setTermsList((L) => L.length < 8 ? [...L, ""] : L);
  const removeTerm = (i) => setTermsList((L) => L.filter((_, idx) => idx !== i));
  const resetTerms = revertTerms;

  useEffect(() => {
    if (termsOpen) loadTerms(termsHeader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsHeader]);

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6" data-testid="admin-sovereign-ledger">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-sm border border-[#8B1A1A]/40 bg-gradient-to-br from-black via-[#0A0906] to-[#0F0C05] p-8">
        <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#8B1A1A]/15 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E] inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Vinci Sovereign Ledger · Admin-Only Archive
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] mt-3 leading-tight">
              The private mirror<br/><span className="text-[#D8B23E]">of every invoice.</span>
            </h2>
            <p className="font-body text-sm text-[#8A8A82] mt-4 max-w-2xl">
              A parallel view of the entire Vinci invoice ledger. Every row can be re-rendered as a PDF with or without the Vinci GSTIN / PAN — the buyer's original artefact stays untouched.
            </p>
          </div>
          <div className="border border-[#8B1A1A]/60 rounded-sm px-4 py-3 bg-black/40 text-right">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#F5B7B7]">Firewall</div>
            <div className="font-body text-xs text-[#C9C9C2] mt-1">Buyers & sellers cannot see this tab.<br/>All GST/PAN toggles stay admin-only.</div>
          </div>
        </div>
      </div>

      {/* Mode switcher + Trade Terms Editor entry */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex border border-white/10 rounded-sm overflow-hidden" role="tablist">
          {[["invoices", "Invoices"], ["quotations", "Quotations"]].map(([k, l]) => (
            <button key={k} onClick={() => { setMode(k); setPage(1); }}
              data-testid={`sovereign-mode-${k}`}
              className={`px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] transition-colors ${
                mode === k ? "bg-[#D8B23E] text-black" : "text-[#C9C9C2] hover:bg-white/5"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={() => openTermsEditor("trade")}
          data-testid="sovereign-open-terms-editor"
          className="inline-flex items-center gap-2 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-4 py-2 font-grotesk text-xs uppercase tracking-[0.22em]">
          <FileText className="w-3.5 h-3.5" /> Trade Terms Editor
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(mode === "quotations" ? [
          ["Quotations", String(quoteKpi.count || 0), "text-[#F5F5F0]"],
          ["Gross Value", inr(quoteKpi.gross_total), "text-[#D8B23E]"],
          ["Bureaucratic layout", "Enabled", "text-[#2FB999]"],
          ["Vinci PAN default", defaults.vinci_pan ? "Shown" : "Hidden", "text-[#F6E4A6]"],
        ] : [
          ["Ledger size", String(kpi.count || 0), "text-[#F5F5F0]"],
          ["Gross Total", inr(kpi.gross_total), "text-[#D8B23E]"],
          ["GST Total", inr(kpi.gst_total), "text-[#2FB999]"],
          ["Paid invoices", String(kpi.paid_count || 0), "text-[#F6E4A6]"],
        ]).map(([k, v, cls]) => (
          <div key={k} className="border border-[#B87333]/20 rounded-sm bg-black/60 p-4">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">{k}</div>
            <div className={`font-serif text-2xl mt-1 ${cls}`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Global Vinci PAN visibility policy */}
      <div className="border border-[#D8B23E]/40 rounded-sm bg-gradient-to-r from-[#0A0906] via-[#0F0C05] to-[#0A0906] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="sovereign-defaults-panel">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] inline-flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Vinci PAN Visibility · Global default
          </div>
          <div className="font-serif text-xl text-[#F5F5F0] mt-1">
            {defaults.vinci_pan ? "Show" : "Hide"} Vinci PAN on every invoice &amp; quotation
          </div>
          <div className="font-body text-[11px] text-[#8A8A82] mt-1">
            {defaults.updated_by
              ? <>Last set by <span className="text-[#F5F5F0]">{defaults.updated_by}</span> on {(defaults.updated_at || "").slice(0, 16).replace("T", " ")}</>
              : "Never changed — currently defaults to the board policy (hidden)."}
          </div>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <span className={`font-body text-sm ${!defaults.vinci_pan ? "text-[#F5F5F0]" : "text-[#8A8A82]"}`}>Hidden</span>
          <span className="relative inline-block w-14 h-6">
            <input type="checkbox" className="sr-only peer"
              disabled={defaultsBusy}
              checked={!!defaults.vinci_pan}
              onChange={(e) => saveDefaults(e.target.checked)}
              data-testid="sovereign-vinci-pan-default" />
            <span className="absolute inset-0 rounded-full bg-white/10 peer-checked:bg-[#D8B23E] transition-colors" />
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-[#F5F5F0] transition-transform ${defaults.vinci_pan ? "translate-x-8" : ""}`} />
          </span>
          <span className={`font-body text-sm ${defaults.vinci_pan ? "text-[#D8B23E]" : "text-[#8A8A82]"}`}>Shown</span>
        </label>
      </div>

      {/* Mode + Trade Terms toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-sm border border-white/10 overflow-hidden">
          {["invoices", "quotations"].map((m) => (
            <button key={m}
              onClick={() => { setMode(m); setPage(1); }}
              data-testid={`sovereign-mode-${m}`}
              className={`px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] ${mode === m ? "bg-[#D8B23E] text-black" : "text-[#C9C9C2] hover:bg-white/5"}`}>
              {m === "invoices" ? "Invoices" : "Quotations"}
            </button>
          ))}
        </div>
        <button onClick={openTermsEditor}
          data-testid="sovereign-terms-open"
          className="inline-flex items-center gap-2 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em]">
          <Scale className="w-3.5 h-3.5" /> Trade Terms Editor
        </button>
      </div>

      {/* Filter strip */}
      <div className="border border-white/10 rounded-sm bg-[#0A0906] p-4 flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[240px]">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">
            <Search className="inline w-3 h-3 mr-1" /> Search
          </div>
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            data-testid="sovereign-search"
            placeholder="Invoice #, buyer email or business, Razorpay payment ID…"
            className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none" />
        </label>
        <label>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">
            <Filter className="inline w-3 h-3 mr-1" /> Header
          </div>
          <select value={header} data-testid="sovereign-header"
            onChange={(e) => { setHeader(e.target.value); setPage(1); }}
            className="bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none">
            {HEADERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Status</div>
          <select value={status} data-testid="sovereign-status"
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#B87333] outline-none">
            {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <button onClick={load} disabled={loading}
          data-testid="sovereign-refresh"
          className="inline-flex items-center gap-2 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-4 py-2 font-grotesk text-xs uppercase tracking-[0.22em]">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reload
        </button>
      </div>

      {/* Quotations Table (only visible in quotations mode) */}
      {mode === "quotations" && (
        <div className="border border-white/10 rounded-sm bg-[#0A0906] overflow-x-auto" data-testid="sovereign-quotations-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#8A8A82] font-grotesk text-[9px] uppercase tracking-[0.2em] border-b border-white/10">
                <th className="text-left py-2 px-3">Quote</th>
                <th className="text-left py-2 px-3">Buyer</th>
                <th className="text-left py-2 px-3">Material</th>
                <th className="text-right py-2 px-3">Total</th>
                <th className="text-left py-2 px-3">Document settings</th>
                <th className="text-left py-2 px-3">Render</th>
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 && !loading && (
                <tr><td colSpan={6} className="py-6 text-center font-body text-sm text-[#8A8A82]">No quotations found.</td></tr>
              )}
              {quotes.map((r) => {
                const t = rowToggles(r.id);
                return (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`sovereign-quote-row-${r.id}`}>
                    <td className="py-3 px-3">
                      <div className="font-mono text-xs text-[#F5F5F0]">{r.quote_number}</div>
                      <div className="text-[10px] text-[#8A8A82]">{(r.created_at || "").slice(0, 10)} · {r.status}</div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-body text-sm text-[#F5F5F0]">{r.buyer_business || r.buyer_name || "—"}</div>
                      <div className="text-[10px] text-[#8A8A82]">{r.buyer_email || r.seller_email || "—"}</div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-body text-xs text-[#F5F5F0]">{r.material || "—"} {r.grade ? `· ${r.grade}` : ""}</div>
                      <div className="text-[10px] text-[#8A8A82]">{r.quantity} {r.unit} @ {inr(r.unit_price)}</div>
                    </td>
                    <td className="py-3 px-3 text-right font-body text-sm text-[#D8B23E]">{inr(r.total)}</td>
                    <td className="py-3 px-3">
                      <div className="flex flex-col gap-1">
                        <ToggleRow label="Vinci GSTIN" checked={t.vinci_gstin} onChange={(v) => setToggle(r.id, "vinci_gstin", v)} testid={`sq-vgst-${r.id}`} />
                        <ToggleRow label="Vinci PAN" checked={t.vinci_pan} onChange={(v) => setToggle(r.id, "vinci_pan", v)} testid={`sq-vpan-${r.id}`} />
                        <ToggleRow label="Buyer GSTIN" checked={t.buyer_gstin} onChange={(v) => setToggle(r.id, "buyer_gstin", v)} testid={`sq-bgst-${r.id}`} />
                        <ToggleRow label="Vinci Contact" checked={t.vinci_contact} onChange={(v) => setToggle(r.id, "vinci_contact", v)} testid={`sq-vc-${r.id}`} />
                        <ToggleRow label="Buyer Contact" checked={t.buyer_contact} onChange={(v) => setToggle(r.id, "buyer_contact", v)} testid={`sq-bc-${r.id}`} />
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <button onClick={() => gen(r)} disabled={busy === r.id}
                        data-testid={`sovereign-quote-render-${r.id}`}
                        className="inline-flex items-center gap-1.5 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                        {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                        Render Quotation
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="p-3 text-xs text-[#8A8A82] font-body">
            {quoteTotal} quotations · Vinci PAN default: {defaults.vinci_pan ? "Shown" : "Hidden"}
          </div>
        </div>
      )}

      {mode === "invoices" && (
      <div className="border border-white/10 rounded-sm bg-[#0A0906] overflow-x-auto">
        <table className="w-full text-sm" data-testid="sovereign-ledger-table">
          <thead>
            <tr className="text-[#8A8A82] font-grotesk text-[9px] uppercase tracking-[0.2em] border-b border-white/10">
              <th className="text-left py-2 px-3">Invoice</th>
              <th className="text-left py-2 px-3">Buyer</th>
              <th className="text-right py-2 px-3">Total</th>
              <th className="text-right py-2 px-3">GST</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Document settings</th>
              <th className="text-left py-2 px-3">Render</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="py-6 text-center font-body text-sm text-[#8A8A82]">
                No invoices match the current filters.
              </td></tr>
            )}
            {rows.map((r) => {
              const t = rowToggles(r.id);
              return (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`sovereign-row-${r.id}`}>
                  <td className="py-3 px-3">
                    <div className="font-mono text-xs text-[#F5F5F0]">{r.invoice_number}</div>
                    <div className="text-[10px] text-[#8A8A82]">{r.header?.toUpperCase()} · {(r.issued_at || "").slice(0, 10)}</div>
                  </td>
                  <td className="py-3 px-3">
                    <div className="font-body text-sm text-[#F5F5F0]">
                      {r.buyer_business || r.buyer_name || "—"}
                    </div>
                    <div className="text-[10px] text-[#8A8A82]">{r.buyer_email}</div>
                  </td>
                  <td className="py-3 px-3 text-right font-body text-sm text-[#D8B23E]">
                    {inr(r.total)}
                  </td>
                  <td className="py-3 px-3 text-right font-body text-xs text-[#2FB999]">
                    {inr(r.gst_total)}
                  </td>
                  <td className="py-3 px-3">
                    <span className={`font-grotesk text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${
                      r.status === "paid" ? "bg-[#2FB999]/20 text-[#2FB999]" :
                      r.status === "sent" ? "bg-[#D8B23E]/20 text-[#F6E4A6]" :
                      "bg-white/10 text-[#C9C9C2]"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex flex-col gap-1">
                      <ToggleRow label="Vinci GSTIN" checked={t.vinci_gstin}
                        onChange={(v) => setToggle(r.id, "vinci_gstin", v)}
                        testid={`sovereign-toggle-vgst-${r.id}`} />
                      <ToggleRow label="Vinci PAN" checked={t.vinci_pan}
                        onChange={(v) => setToggle(r.id, "vinci_pan", v)}
                        testid={`sovereign-toggle-vpan-${r.id}`} />
                      <ToggleRow label="Buyer GSTIN" checked={t.buyer_gstin}
                        onChange={(v) => setToggle(r.id, "buyer_gstin", v)}
                        testid={`sovereign-toggle-bgst-${r.id}`} />
                      <ToggleRow label="Vinci Contact" checked={t.vinci_contact}
                        onChange={(v) => setToggle(r.id, "vinci_contact", v)}
                        testid={`sovereign-toggle-vc-${r.id}`} />
                      <ToggleRow label="Buyer Contact" checked={t.buyer_contact}
                        onChange={(v) => setToggle(r.id, "buyer_contact", v)}
                        testid={`sovereign-toggle-bc-${r.id}`} />
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex flex-col gap-1.5">
                      <button onClick={() => gen(r)} disabled={busy === r.id}
                        data-testid={`sovereign-render-${r.id}`}
                        className="inline-flex items-center gap-1.5 border border-[#B87333] hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                        {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                        Render PDF
                      </button>
                      <button onClick={() => openShare(r)}
                        data-testid={`sovereign-share-${r.id}`}
                        className="inline-flex items-center gap-1.5 bg-[#D8B23E] hover:bg-[#F6E4A6] text-black rounded-sm px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                        <Send className="w-3 h-3" /> Share
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Pagination */}
        <div className="flex items-center justify-between p-3 text-xs text-[#8A8A82] font-body">
          <div>Page {page} / {pages} · {total} invoices in ledger</div>
          <div className="flex gap-2">
            <button disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="border border-white/10 rounded-sm px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] disabled:opacity-40">
              Prev
            </button>
            <button disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              className="border border-white/10 rounded-sm px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] disabled:opacity-40">
              Next
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Audit strip */}
      <div className="border border-white/10 rounded-sm bg-[#0A0906] p-4" data-testid="sovereign-audit">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333] mb-3 inline-flex items-center gap-2">
          <HistoryIcon className="w-3.5 h-3.5" /> Sovereign Render Audit · last 8
        </div>
        {audit.length === 0 ? (
          <div className="text-center py-3 text-xs text-[#8A8A82] font-body">
            No sovereign renders yet. Every generation you fire from this page will be logged here.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#8A8A82] font-grotesk uppercase tracking-[0.18em] text-[9px]">
                <th className="text-left py-1.5 px-2">When</th>
                <th className="text-left py-1.5 px-2">By</th>
                <th className="text-left py-1.5 px-2">Invoice</th>
                <th className="text-left py-1.5 px-2">Buyer</th>
                <th className="text-left py-1.5 px-2">Toggles</th>
                <th className="text-right py-1.5 px-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-t border-white/5">
                  <td className="py-1.5 px-2 text-[#C9C9C2]">{(a.at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-1.5 px-2 text-[#F5F5F0]">{a.by || "—"}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-[#F5F5F0]">{a.invoice_number}</td>
                  <td className="py-1.5 px-2 text-[#8A8A82]">{a.buyer_email || "—"}</td>
                  <td className="py-1.5 px-2">
                    <span className={`mr-1 ${a.include_gst ? "text-[#2FB999]" : "text-[#8A8A82]"}`}>GST:{a.include_gst ? "on" : "off"}</span>
                    <span className={a.include_pan ? "text-[#2FB999]" : "text-[#8A8A82]"}>PAN:{a.include_pan ? "on" : "off"}</span>
                  </td>
                  <td className="py-1.5 px-2 text-right text-[#D8B23E]">{inr(a.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Share modal */}
      {sharing && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
              data-testid="sovereign-share-modal">
          <div className="relative w-full max-w-xl border border-[#D8B23E]/60 rounded-sm bg-[#0B0B0C] p-6 shadow-2xl">
            <button onClick={() => setSharing(null)}
              className="absolute top-3 right-3 text-[#8A8A82] hover:text-[#F5F5F0]"><X className="w-4 h-4" /></button>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] inline-flex items-center gap-2 mb-2">
              <Mail className="w-3.5 h-3.5" /> Share Sovereign Invoice
            </div>
            <h3 className="font-serif text-2xl text-[#F5F5F0] mb-1">{sharing.invoice_number}</h3>
            <p className="font-body text-xs text-[#8A8A82] mb-4">
              Sends the currently-toggled bureaucratic PDF from Vinci Executive Command directly to your chosen recipient. Every send is logged in the audit trail.
            </p>
            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Recipient · TO</div>
              <input type="email" data-testid="sovereign-share-to"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#D8B23E] outline-none"
                value={shareTo} onChange={(e) => setShareTo(e.target.value)} />
              <div className="text-[10px] text-[#8A8A82] mt-1">Default: buyer on file — override to send to anyone.</div>
            </label>
            <label className="block mb-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">CC (comma-separated)</div>
              <input data-testid="sovereign-share-cc"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#D8B23E] outline-none"
                value={shareCc} onChange={(e) => setShareCc(e.target.value)} />
            </label>
            <label className="block mb-4">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-1">Personal note (optional)</div>
              <textarea rows={3} data-testid="sovereign-share-note"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#D8B23E] outline-none"
                value={shareNote} onChange={(e) => setShareNote(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSharing(null)} disabled={shareBusy}
                className="border border-white/20 hover:border-white/40 text-[#C9C9C2] rounded-sm px-4 py-2 font-grotesk text-xs uppercase tracking-[0.22em]">
                Cancel
              </button>
              <button onClick={doShare} disabled={shareBusy}
                data-testid="sovereign-share-send"
                className="inline-flex items-center gap-2 bg-[#D8B23E] hover:bg-[#F6E4A6] text-black rounded-sm px-5 py-2 font-grotesk text-xs uppercase tracking-[0.24em]">
                {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {shareBusy ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trade Terms Editor modal */}
      {termsOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
              data-testid="sovereign-terms-modal">
          <div className="relative w-full max-w-3xl border border-[#D8B23E]/60 rounded-sm bg-[#0B0B0C] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => setTermsOpen(false)}
              className="absolute top-3 right-3 text-[#8A8A82] hover:text-[#F5F5F0]"><X className="w-4 h-4" /></button>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] inline-flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5" /> Vinci Trade Terms · Editor
            </div>
            <h3 className="font-serif text-2xl text-[#F5F5F0] mb-1">Six binding clauses per header</h3>
            <p className="font-body text-xs text-[#8A8A82] mb-4">
              These clauses render inside every bureaucratic invoice or quotation PDF for the selected header. Blank the list to revert to the board-defined defaults.
            </p>
            {/* Header switcher */}
            <div className="inline-flex border border-white/10 rounded-sm overflow-hidden mb-4">
              {["trade", "service", "commission", "custom"].map((h) => (
                <button key={h} onClick={() => switchTermsHeader(h)}
                  data-testid={`sovereign-terms-header-${h}`}
                  className={`px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] ${
                    termsHeader === h ? "bg-[#D8B23E] text-black" : "text-[#C9C9C2] hover:bg-white/5"}`}>
                  {h}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#8A8A82] font-body mb-3">
              {termsMeta.is_custom
                ? <>Custom · last saved by <span className="text-[#F5F5F0]">{termsMeta.updated_by}</span> {termsMeta.updated_at ? "on " + termsMeta.updated_at.slice(0, 16).replace("T", " ") : ""}</>
                : "Currently on Vinci board defaults · edit + save to override for this header only."}
            </div>
            <div className="space-y-3">
              {termsList.map((t, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] pt-2">§{i + 1}</span>
                  <textarea rows={2} value={t}
                    data-testid={`sovereign-term-${i}`}
                    onChange={(e) => setTermAt(i, e.target.value)}
                    className="flex-1 bg-black border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:border-[#D8B23E] outline-none" />
                  <button onClick={() => removeTerm(i)}
                    className="text-[#8A8A82] hover:text-[#C0392B] pt-2"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={addTerm} disabled={termsList.length >= 8}
                className="inline-flex items-center gap-1 border border-[#B87333]/60 hover:bg-[#B87333]/10 text-[#D8B23E] rounded-sm px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                <Plus className="w-3 h-3" /> Add clause
              </button>
              <button onClick={resetTerms} disabled={termsBusy}
                className="ml-auto border border-white/10 hover:border-white/40 text-[#C9C9C2] rounded-sm px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
                Reset to board defaults
              </button>
            </div>
            <div className="flex justify-end gap-2 mt-5 border-t border-white/10 pt-4">
              <button onClick={() => setTermsOpen(false)} disabled={termsBusy}
                className="border border-white/20 hover:border-white/40 text-[#C9C9C2] rounded-sm px-4 py-2 font-grotesk text-xs uppercase tracking-[0.22em]">
                Close
              </button>
              <button onClick={saveTerms} disabled={termsBusy}
                data-testid="sovereign-terms-save"
                className="inline-flex items-center gap-2 bg-[#D8B23E] hover:bg-[#F6E4A6] text-black rounded-sm px-5 py-2 font-grotesk text-xs uppercase tracking-[0.24em]">
                {termsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Save {termsHeader.toUpperCase()} terms
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <span className="relative inline-block w-8 h-4">
        <input type="checkbox" className="sr-only peer"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testid} />
        <span className="absolute inset-0 rounded-full bg-white/10 peer-checked:bg-[#2FB999]/70 transition-colors" />
        <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-[#F5F5F0] transition-transform ${checked ? "translate-x-4" : ""}`} />
      </span>
      <span className={`font-body text-[11px] ${checked ? "text-[#2FB999]" : "text-[#8A8A82]"}`}>{label}: {checked ? "Yes" : "No"}</span>
    </label>
  );
}
